import { useMemo } from "react";
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from "@nextui-org/react";
import { MoreHorizontal, ArrowRight, Download, History, RefreshCw } from "lucide-react";
import type { Assessment, ActionType } from "../types";
import { ACTIONS } from "../constants";

const iconMap: Record<string, React.FC<{ className?: string }>> = {
  ArrowRight,
  Download,
  History,
  RefreshCw,
};

interface ActionsDropdownProps {
  assessment: Assessment;
  vendorName: string;
  onAction: (action: ActionType, assessment: Assessment) => void;
}

export function ActionsDropdown({ assessment, vendorName, onAction }: ActionsDropdownProps) {
  const disabledKeys = useMemo(() => {
    const keys: string[] = [];

    if (!assessment.final_report_available) {
      keys.push("download_report", "download_summary");
    }

    if (!assessment.permissions.can_reassess) {
      keys.push("reassess");
    }

    return keys;
  }, [assessment]);

  const productName = assessment.multi_file_vendor_assessment.product_name || "assessment";

  return (
    <Dropdown
      classNames={{
        content: "p-1 min-w-[188px] bg-white border border-[#E2E8F0] rounded-md shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)]",
      }}
    >
      <DropdownTrigger>
        <Button
          isIconOnly
          variant="light"
          size="sm"
          className="min-w-5 w-5 h-5 p-0.5 rounded hover:bg-[#E2E8F0] transition-colors"
          aria-label={`Actions for ${productName}`}
        >
          <MoreHorizontal className="h-4 w-4 text-[#62748E]" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Assessment actions"
        disabledKeys={disabledKeys}
        onAction={(key) => onAction(key as ActionType, assessment)}
        classNames={{
          base: "p-0",
          list: "gap-0",
        }}
        itemClasses={{
          base: "p-2 gap-2 h-9 rounded data-[hover=true]:bg-[#F1F5F9]",
          title: "font-['Inter'] font-normal text-sm leading-5 text-[#314158]",
        }}
      >
        {ACTIONS.map(({ key, label, icon }) => {
          const Icon = iconMap[icon];
          return (
            <DropdownItem key={key} startContent={Icon ? <Icon className="h-4 w-4 text-[#62748E]" /> : null}>
              {label}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
}
